import * as React from "react";
import {
    Box,
    Container,
    Typography,
    Stack,
    Divider,
    Chip,
    Avatar,
    Paper,
    Link,
} from "@mui/material";

import informationTheoryImg from "../assets/images/information-theory-banner.jpg";


import stage1Img from "../assets/images/info-drummer.jpg";
import stage2Img from "../assets/images/info-dictionary.jpg";
import stage3Img from "../assets/images/info-analyticalEngine.jpg";
import stage4Img from "../assets/images/info-telegraph.jpg";
import stage5Img from "../assets/images/info-digitalCircuit.jpg";
import stage6Img from "../assets/images/info-entropy.jpg";
import stage7Img from "../assets/images/info-cybernetics.jpg";
import stage8Img from "../assets/images/info-thermodynamics.jpg";
import stage9Img from "../assets/images/info-algorithm.jpg";
import stage10Img from "../assets/images/info-10.jpg";
import {useEffect} from "react";
import {Helmet} from "react-helmet-async";

const fontFamily =
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

type Stage = {
    number: number;
    title: string;
    kicker?: string;
    body: React.ReactNode;
    image: string;
    imageAlt: string;
};

function StageBlock({
                        stage,
                        align = "left",
                    }: {
    stage: Stage;
    align?: "left" | "right";
}) {
    const isLeft = align === "left";

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 3 },
            }}
        >
            {/* flow-root is the cleanest float container/clearfix */}
            <Box sx={{ display: "flow-root" }}>
                {/* Float image */}
                <Box
                    sx={{
                        float: { xs: "none", md: isLeft ? "left" : "right" },
                        width: { xs: "100%", md: "20%" }, // 1/5 width
                        mr: isLeft ? { md: 2.5 } : 0,
                        ml: !isLeft ? { md: 2.5 } : 0,
                        mb: { xs: 2, md: 1.5 },
                    }}
                >
                    <Box
                        component="img"
                        src={stage.image}
                        alt={stage.imageAlt}
                        // loading="lazy"
                        // width="1600"
                        // height="900"
                        sx={{
                            width: "100%",
                            aspectRatio: "1 / 1", // compact editorial look
                            objectFit: "cover",
                            borderRadius: 2,
                            border: 1,
                            borderColor: "divider",
                            display: "block",
                        }}
                    />
                </Box>

                {/* IMPORTANT: normal block flow (NOT Stack) so text truly wraps */}
                <Box>
                    {/* Small header row can be flex */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            flexWrap: "wrap",
                            mb: 1,
                        }}
                    >
                        <Chip
                            size="small"
                            label={`Stage ${stage.number}`}
                            sx={{
                                fontFamily,
                                fontWeight: 700,
                                borderRadius: 999,
                                bgcolor: "grey.100",
                            }}
                        />
                        {stage.kicker ? (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily,
                                    color: "text.secondary",
                                    fontWeight: 600,
                                }}
                            >
                                {stage.kicker}
                            </Typography>
                        ) : null}
                    </Box>

                    <Typography
                        variant="h5"
                        sx={{
                            fontFamily,
                            fontWeight: 900,
                            letterSpacing: -0.4,
                            lineHeight: 1.25,
                            fontSize: { xs: "1.1rem", sm: "1.3rem" },
                            mb: 1,
                        }}
                    >
                        {stage.title}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily,
                            lineHeight: 1.85,
                            fontSize: { xs: "0.98rem", sm: "1.02rem" },
                        }}
                    >
                        {stage.body}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
}

export default function InformationTheory() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    const stages: Stage[] = [
        {
            number: 1,
            title: "The Acoustic Bit: Talking Drums and the Birth of Redundancy",
            kicker: "Redundancy beats noise",
            image: stage1Img,
            imageAlt: "Talking drums in a forest setting",
            body: (
                <>
                    Long before the telegraph, the first “high-speed” long-distance
                    communication network existed in the rainforests of Africa. The
                    “talking drums” were not merely signaling devices; they were a complex,
                    tonal language system.
                    <br />
                    <br />
                    The genius of the drum was how it solved the problem of ambiguity.
                    Because drumbeats lack the vowels and consonants of speech, a single
                    beat could mean a dozen things. The solution? <b>Redundancy</b>. To say
                    “moon,” a drummer wouldn’t just strike once; they would send a poetic
                    phrase like “the moon that looks down on the earth.” This added “bits”
                    to the message to ensure the signal survived the “noise” of the jungle.
                    This was the first practical application of what Claude Shannon would
                    later mathematically define as “error-correcting code.” Information
                    complexity began here, by learning that to be understood, you must
                    often say more than what is strictly necessary.
                </>
            ),
        },
        {
            number: 2,
            title: "The Persistence of the Word: The Dictionary as the First Compiler",
            kicker: "Standardization scales meaning",
            image: stage2Img,
            imageAlt: "Old dictionary pages and handwritten notes",
            body: (
                <>
                    If drums were the first “streaming” service, writing was the first
                    “storage” medium. But writing was chaotic until the creation of the
                    dictionary. In Gleick’s view, the dictionary, specifically the
                    “wordbooks” of the 17th century like Robert Cawdrey’s, acted as the
                    first word compiler.
                    <br />
                    Before dictionaries, spelling was a suggestion and meaning was fluid.
                    The dictionary “compiled” the fuzzy logic of human speech into a
                    standardized, searchable API. It brought a “logic and order” to
                    language that allowed for the scaling of knowledge. Once you have a
                    dictionary, you have a fixed set of symbols. You have a “lookup
                    table.” This standardization was the essential precursor to any form
                    of automated thought; you cannot program a machine to think if the
                    words it uses change their meaning every fifty miles.
                </>
            ),
        },
        {
            number: 3,
            title: 'The Analytical Engine: Mapping Thought into "Wheel-Work"',
            kicker: "Symbols become executable",
            image: stage3Img,
            imageAlt: "Victorian-era mechanical computing gears",
            body: (
                <>
                    In the 19th century, Charles Babbage and Ada Lovelace made the
                    ultimate leap: they realized that logic could be physical. Babbage’s
                    Analytical Engine was the first design for a general-purpose computer.
                    It featured what we now call a theoretical ALU. He called it “The
                    Mill” and a separate “Store” (Memory).
                    <br />
                    Lovelace’s insight was even more profound than Babbage's. She realized
                    that the machine didn’t just have to process numbers; <b>It could process
                    any symbolic information. If you could map music, logic, or language
                    onto the “wheel-work” of the gears, the machine could “think” about
                    them</b>. This was the transition from “calculating” to “computing.”
                    Information was no longer just a record of the past; it was a process
                    that could be executed.
                </>
            ),
        },
        {
            number: 4,
            title: "A Nervous System for the Earth: The Telegraphic Medium",
            kicker: "Bits break free of matter",
            image: stage4Img,
            imageAlt: "Telegraph wires and Morse key",
            body: (
                <>
                    The telegraph was the “Great Divide.” Before it, information moved at
                    the speed of a horse or a ship. It was tied to continuous matter. The
                    telegraph changed the medium to discrete electrical pulses.
                    <br />
                    This was the first time “bits” (dots and dashes) moved independently
                    of physical objects. It created a “global nervous system.” The
                    telegraph forced a compression of language (telegraphese) and a new
                    awareness of time. Because information could move instantly, the
                    “present moment” expanded. For the first time,<b> a merchant in London
                    knew the price of cotton in New York <i>now</i>, not three weeks from
                    now.</b> The world became synchronized by the pulse of the wire.
                </>
            ),
        },
        {
            number: 5,
            title: "New Wires, New Logic: The Marriage of Boole and Electricity",
            kicker: "True/False becomes hardware",
            image: stage5Img,
            imageAlt: "Boolean logic diagram and electrical relays",
            body: (
                <>
                    While the telegraph wires were spreading, George Boole was reinventing
                    logic. He realized that the ancient propositional logic of Aristotle
                    could be treated as a form of algebra. By reducing all logical
                    statements to “True” or “False” (1 or 0), he created a mathematical
                    language for the “Laws of Thought.”
                    <br />
                    However, Boole’s logic sat on a shelf for decades as a philosophical
                    curiosity. It wasn’t until the 20th century that Claude Shannon
                    realized Boole’s “0 and 1” logic was the perfect language for the
                    “On/Off” states of electrical switches. This was the Great Synthesis:
                    electricity could not only power a lightbulb, it could also carry a
                    logical argument.
                </>
            ),
        },
        {
            number: 6,
            title: 'The Information Theory Revolution: Defining the "Bit"',
            kicker: "Uncertainty becomes measurable",
            image: stage6Img,
            imageAlt: "Claude Shannon style schematic and binary stream",
            body: (
                <>
                    In 1948, Claude Shannon published <i>A Mathematical Theory of
                    Communication</i>, and the world changed forever. Shannon gave us the
                    bit as the fundamental unit of information, but more importantly, he
                    decoupled information from meaning.
                    <br />
                    In Shannon’s world, a bit is simply a choice that resolves uncertainty.
                    Whether that bit represents a pixel in a photo or a letter in a DNA
                    sequence is irrelevant to the theory. This allowed us to measure
                    information complexity across domains. He also introduced entropy to
                    communication: <b>the idea that the more random or surprising a message
                    is, the more information it contains.</b>
                    <br/>
                    <i>This was the moment information
                        became a physical quantity, as real as energy or mass.</i>
                </>
            ),
        },
        {
            number: 7,
            title: "The Cybernetics Movement: Information as Control",
            kicker: "Feedback loops create order",
            image: stage7Img,
            imageAlt: "Feedback loop diagram and early control systems",
            body: (
                <>
                    As the 1950s dawned, the cybernetics movement, led by Norbert Wiener,
                    took Shannon’s ideas and applied them to time and movement.
                    <b>Cybernetics
                    is the study of “control and communication in the animal and the
                        machine.”</b>
                    <br />
                    The core idea was the feedback loop. Information isn’t just a one-way
                    stream; it is a circular process. Sensors gather information, the
                    system processes it, and an effector acts on the world, which creates
                    new information for the sensors. This perspective turned the “mind”
                    into an information-processing system. It suggested that,
                    <br/>
                    <i>a thermostat
                    and a human brain are doing the same thing: using information to
                        maintain order against the chaos of the environment.</i>
                </>
            ),
        },
        {
            number: 8,
            title: "The Physics of Information: Thermodynamics and Entropy",
            kicker: "Information has a cost",
            image: stage8Img,
            imageAlt: "Thermodynamics imagery and heat dissipation",
            body: (
                <>
                    Gleick’s history takes an unexpected, brilliant turn into physics. We learned
                    that information is not “free.” Rolf Landauer and Charles Bennett
                    showed that erasing a bit of information releases heat. This linked
                    information theory directly to the second law of thermodynamics.
                    <br />
                    This is the Maxwell’s Demon problem: to sort “hot” molecules from
                    “cold” ones, a demon needs information. But to get that information
                    and store it, the demon must eventually erase its memory, which
                    creates more heat than it saves. Information is a physical thing. It
                    has weight. It has a cost. <br/>
                    <i>The complexity of our digital age is
                    literally warming the planet not just through the power used by
                        servers, but as a fundamental law of the universe.</i>
                </>
            ),
        },
        {
            number: 9,
            title: "Computation & The Infinite: Turing, Chaitin, and Memes",
            kicker: "Limits define intelligence",
            image: stage9Img,
            imageAlt: "Turing machine tape and abstract code",
            body: (
                <>
                    Alan Turing gave us the Turing Machine. The proof that a simple machine
                    reading symbols on a tape could compute anything that is computable.
                    But he also showed there are limits. The Busy Beaver problem hints
                    that there are some things we can never know or calculate; some
                    information is undecidable.
                    <br />
                    Gregory Chaitin took this further with algorithmic information theory,
                    defining the complexity of a string as the length of the shortest
                    program that can produce it. At the same time, Richard Dawkins
                    introduced the meme. Information was now seen as a replicator. Just as
                    DNA replicates in cells, memes (ideas, songs, catchphrases) replicate
                    in the infosphere of human minds. Information became alive, driven by
                    evolutionary pressure to survive and spread.
                </>
            ),
        },
        {
            number: 10,
            title: "The 2026 Frontier: Managing the Flood",
            kicker: "From storage to translation",
            image: stage10Img,
            imageAlt: "Neural interfaces, AI models, robots and drones",
            body: (
                <>
                    We have moved past the history of the bit and into the era of
                    integrated intelligence. In 2026, our view of the world is no longer
                    about storing information, but about seamless translation between the
                    digital and the physical.
                    <br />
                    <br />
                    <b>The LLM as the Universal Text Compiler, at least for now.</b> If the dictionary was the first
                    "word compiler," the Large Language Model (LLM) is the final one. And if we solemly believe this to be
                    true, we find ourselves in a position strikingly similar to a Chevalier of the Maison du Roi in 1675,
                    tasked with the protection of Marie Thérèse of France, the daughter of the Sun King.<br/>
                    As this knight stands sentinel, he watches the King’s advisors; the early "information architects",
                    poring over the recently perfected Dictionnaire de l'Académie française. To him, civilization has
                    reached its absolute terminal peak. He watches them use these standardized "compilers" to do Statecraft:
                    drafting the <i>Code Louis</i> to unify French law into a single searchable logic; and Clandestine Warfare:
                    utilizing the "Great Cipher" of the Rossignols to encrypt letters that decide the fate of the Dutch War.
                    <br/>
                    <br/>
                    To the Chevalier, the dictionary is not just a book; it is a divine machine of order that has finally tamed
                    the "noise" of the world. <i>He believes that by pinning every word to a single definition, his King has achieved
                    the ultimate control over reality.</i>
                    <br/>
                    <br/>

                    Today, we look at the Large Language Model with that same breathless wonder, having moved from programming
                    computers with rigid, brittle code to prompting them with the raw redundancy of human language. By
                    ingesting the totality of written history, LLMs have transformed our collective "noise" into a
                    multidimensional map of thought, acting as the final, supreme wordbooks of the digital age. We feel,
                    as the Chevalier did, that we have finally mapped the boundaries of the "infosphere."
                    <br />
                    <br />
                    We stand on the precipice of a reality where intent becomes execution without the friction of language,
                    proving the "word" was always a bottleneck. Just as the telegraph's discrete pulses once obliterated the
                    Chevalier’s world of ink, our era of prompting is merely a transient waypoint before the medium of
                    language disappears. The 2026 frontier of neural integration and autonomous robotics suggests the future
                    is not about mastering the flood, but transcending the interface entirely. This informational deluge is
                    not a disaster to be survived, but the necessary substrate for an unimaginably more complex evolution.
                    <br/>
                    <br/>
                    <Divider sx={{ my: 2 }} />
                    <b>Robots and Drones: Information with Legs.</b> The cybernetics of the
                    1950s has matured into autonomous infrastructure.<br/>
                    <i>Sure, Ada Lovelace was more interested in Babbage's Analytical Machine than the <b>Silver Lady</b>
                        , but Bool's logic also sat on the same shelf till a better suited space-timewise person decided to
                        pursue the unknown, this time with robotics</i><br/>
                    Self-driving cars and drone swarms are essentially information endowed with spatial intelligence.
                    They do not just move through space; they calculate it, turning the chaotic physical world into a
                    high-fidelity, real-time bitmap. By processing millions of variables through the "Laws of Thought"
                    Boole pioneered, these machines navigate the entropy of a city street with the same discrete precision
                    that once lived only in the abstract pulses of a telegraph wire. In 2026, the distinction between
                    a data packet and a moving vehicle has blurred; the robot is simply a thought that has learned how to walk.
                    <br/>
                    <Divider sx={{ my: 2 }} />
                    <b>Brain-Machine Interfaces (BMI): The compliment of the Medium.</b> For
                    thousands of years, the bottleneck of information was the human body.
                    With modern BMIs (like Neuralink and Synchron), we are <i>supplimenting</i> the
                    medium. Moving from bits on a wire to spikes in a neuron.
                    <br />
                    In 2026, we are shifting from the external transmission of bits on a wire to the internal synchronization
                    of spikes in a neuron, finally understanding the biological bottleneck. The interface is no longer a tool
                    we hold, but a bridge merging symbolic processing with the global infosphere, treating paralysis and
                    disability as <b><i>signal-processing refactoring</i></b> rather than biological failures. By decoupling the mental
                    "message" from the physical "matter," we are moving toward a future where intent becomes execution
                    without the friction of language.
                    <br/>
                    <br />
                    <br />
                    <Divider sx={{ my: 2 }} />
                    We have spent centuries trying to solve the problem of how to send a message through the noise of the
                    physical world. Now that the message has become the world itself, perhaps the most revolutionary act
                    of information theory is knowing when to switch the receiver off. <i> You can start by touching some
                    grass as you reflect on what you've read, so to speak.</i>
                    <br/>
                    We are no longer just observers of information history, we are becoming the information
                    itself.

                    <br/>
                    <br/>
                    <br/>
                    <i>No, this blog doesn't have ad analytics lol</i>
                </>
            ),
        },
    ];

    return (
        <>
            <Helmet>
                <title>
                    When Information Learned to Think
                </title>

                <meta
                    name="description"
                    content="Information theory’s journey from West Africa's talking drums(700AD) and telegraphs(1800s) to bits, logic, AI, and the human brain(21st Century)."
                />

                <link
                    rel="canonical"
                    href="https://aggreylelei.com/blog/information-theory/"
                />

                <meta property="og:type" content="article" />
                <meta
                    property="og:title"
                    content="When Information Learned to Think"
                />
                <meta
                    property="og:description"
                    content="Information theory’s journey from West Africa's talking drums(700AD) and telegraphs(1800s) to bits, logic, AI, and the human brain(21st Century)."
                />
                <meta
                    property="og:url"
                    content="https://aggreylelei.com/blog/information-theory"
                />
                <meta
                    property="og:image"
                    content="https://aggreylelei.com/og-information-theory.jpg"
                />

                <meta property="article:published_time" content="2026-03-31T09:00:00+03:00" />
                <meta property="article:modified_time" content="2026-03-31T09:00:00+03:00" />
                <meta property="article:author" content="Aggrey Lelei" />
                <meta property="og:site_name" content="Aggrey Lelei" />

                <meta name="author" content="Aggrey Lelei" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="When Information Learned to Think"
                />
                <meta
                    name="twitter:description"
                    content="Information theory’s journey from West Africa's talking drums(700AD) and telegraphs(1800s) to bits, logic, AI, and the human brain(21st Century)."
                />
                <meta
                    name="twitter:image"
                    content="https://aggreylelei.com/og-information-theory.jpg"
                />
            </Helmet>
            <Box
                component="main"
                sx={{
                    fontFamily,
                    bgcolor: "background.default",
                    color: "text.primary",
                    py: { xs: 5, md: 8 },
                }}
            >
                <Container maxWidth="md">
                    {/* Header */}
                    <Stack spacing={2.25} sx={{ mb: 4 }}>
                        {/* Hero banner */}
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                border: 1,
                                borderColor: "divider",
                                mb: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: "grey.100",
                                    aspectRatio: "16 / 4",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={informationTheoryImg}
                                    alt="Article hero"
                                    // loading="lazy"
                                    // width="1600"
                                    // height="900"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        </Paper>

                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontFamily,
                                fontWeight: 900,
                                letterSpacing: -0.8,
                                lineHeight: 1.12,
                                fontSize: { xs: "1.3rem", sm: "1.9rem", md: "2.3rem" },
                            }}
                        >
                            The Information Arc: From Talking Drums to the Neural Frontier
                        </Typography>

                        <Typography
                            variant="h6"
                            component="p"
                            sx={{
                                fontFamily,
                                fontWeight: 400,
                                color: "text.secondary",
                                lineHeight: 1.5,
                                fontSize: { xs: "1.05rem", sm: "1.15rem" },
                            }}
                        >
                            Information is not just the stuff in books or the data on your hard
                            drive. As James Gleick argues in his seminal work,{" "}
                            <i>The Information</i>, it’s the blood, the fuel, and the very fabric
                            of the universe. To understand where we are going with AI and
                            Brain-Machine Interfaces (BMI), we must first understand how we
                            learned to separate the “message” from the “matter.”
                        </Typography>

                        <Typography
                            variant="body2"
                            component="p"
                            sx={{
                                fontFamily,
                                fontStyle: "italic",
                                fontWeight: 400,
                                color: "text.secondary",
                                lineHeight: 1.55,
                                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                                opacity: 0.9,
                                mt: 1,
                            }}
                        >
                            This piece is inspired by <b>The Information</b> by James Gleick,
                            which traces the evolution of information from early communication
                            to modern information theory. Through concepts like entropy, noise,
                            and compression, the book reveals how information underpins science
                            and technology. It’s a highly recommended read if you want a deeper
                            intuition for data, signals, and meaning. You can find it on{" "}
                            <Link
                                href="https://www.amazon.com/dp/1400096235"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="always"
                                sx={{ color: "inherit", fontFamily }}
                            >
                                Amazon here
                            </Link>
                            .
                        </Typography>

                        {/* Author row */}
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: "grey.900",
                                    color: "common.white",
                                    fontFamily,
                                    fontWeight: 800,
                                }}
                            >
                                AL
                            </Avatar>
                            <Stack spacing={0.25}>
                                <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                                    <Typography sx={{ fontFamily, fontWeight: 800 }}>
                                        Aggrey
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily, color: "text.secondary" }}
                                    >
                                        Information Theory · Shannon Entropy · Cybernetics · Codes &
                                        Compression · Robotics · History of Computing · Philosophy of Information
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    sx={{ fontFamily, color: "text.secondary" }}
                                >
                                    10 min read · June 20, 2026
                                </Typography>
                            </Stack>
                        </Stack>

                        <Divider sx={{ mt: 1 }} />
                    </Stack>

                    {/* Body */}
                    <Stack spacing={3.25} sx={{ fontFamily }}>
                        {stages.map((stage, idx) => (
                            <StageBlock
                                key={stage.number}
                                stage={stage}
                                align={idx % 2 === 0 ? "left" : "right"}
                            />
                        ))}

                        <Divider />

                        {/*/!* Closing note *!/*/}
                        {/*<Paper*/}
                        {/*    elevation={0}*/}
                        {/*    sx={{*/}
                        {/*        borderRadius: 3,*/}
                        {/*        border: 1,*/}
                        {/*        borderColor: "divider",*/}
                        {/*        p: { xs: 2.25, sm: 3 },*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    <Stack spacing={1.25}>*/}
                        {/*        <Typography*/}
                        {/*            variant="h6"*/}
                        {/*            sx={{*/}
                        {/*                fontFamily,*/}
                        {/*                fontWeight: 900,*/}
                        {/*                letterSpacing: -0.2,*/}
                        {/*            }}*/}
                        {/*        >*/}
                        {/*            The arc is clear*/}
                        {/*        </Typography>*/}
                        {/*        <Typography*/}
                        {/*            variant="body1"*/}
                        {/*            sx={{*/}
                        {/*                fontFamily,*/}
                        {/*                color: "text.secondary",*/}
                        {/*                lineHeight: 1.75,*/}
                        {/*            }}*/}
                        {/*        >*/}
                        {/*            From drums that fought noise with redundancy, to telegraphs that*/}
                        {/*            freed messages from matter, to LLMs and BMIs that compress and*/}
                        {/*            translate thought itself—the story is the same: information keeps*/}
                        {/*            demanding more bandwidth, more structure, and less friction.*/}
                        {/*            The future isn’t “finding information.” It’s deciding what*/}
                        {/*            deserves to reach your mind at all.*/}
                        {/*        </Typography>*/}
                        {/*    </Stack>*/}
                        {/*</Paper>*/}
                    </Stack>
                </Container>
            </Box>
        </>
    );
}